/**
 * Shortens the Studio tab's Forge blocks before any sung line is deleted (AUTO-086).
 *
 * The lyrics box holds 5,000 characters and the Forge blocks (Riff DNA, vocal / scream / Deep-Dive
 * physics, META lines, Solo Choreography) share it with the song. When the total ran over, the
 * last-resort trim (trimLyricsBodyToHardCap) deleted sung lines from the end and kept one line per
 * section, so a song with a heavy rig came out as one line of each chorus. The squeeze in
 * block1Overhead.ts only knows Studio Team's cue and dossier blocks, so it did nothing here.
 *
 * Order, stopping as soon as the body fits (Frank, 2026-10-24: solo detail matters least, not every
 * song needs a scream):
 *   1. Solo Choreography per-bar detail lines
 *   2. Solo Choreography bar labels cut short
 *   3. the {SCREAM-PHYSICS} block
 *   4. Deep-Dive layers one at a time: breath, then aging, then dialect
 * Never touched: {LAYER-1 VOCAL PHYSICS}, section tags, the metadata bleed token, sung lines.
 */
import { clipAtClause } from './block1Overhead';

const MAJOR_SECTION_RE = /^\[[^\]]*\b(?:Intro|Outro|Verse|Chorus|Bridge|Break|Solo)\b[^\]]*\]\s*$/i;
const ONLY_BRACKETS_RE = /^(?:[[{][^\]}]*[\]}]\s*)+$/;
const ONLY_PAREN_RE = /^\([^)]*\)$/;

const SOLO_HEADER_RE = /^\{LAYER-6 SOLO CHOREOGRAPHY\}$/;
const SOLO_INSTRUMENT_RE = /^\[Solo_Instrument:/;
const BAR_LABEL_RE = /^\[Bar \d+-\d+:/i;
/** Structure tags that can follow a solo block directly; a detail line never looks like these. */
const STRUCTURE_AFTER_SOLO_RE = /^\[(?:Doom Riff|Riff|Heavy|Instrumental|Cold Stop|End)\b/i;
const HAS_BAR_COUNT_RE = /[—-]\s*\d+\s*bars\b/i;

const SCREAM_HEADER_RE = /^\{SCREAM-PHYSICS\b/;
const DEEP_DIVE_HEADER_RE = /^\{DEEP-DIVE VOCAL LAYERS\}$/;
/** Deep-Dive layers in the order they are given up. */
const DEEP_DIVE_LAYER_RES = [/^\{BREATH-PHYSICS\b/, /^\{AGING-PHYSICS\b/, /^\{DIALECT-PHYSICS\b/];

const LABEL_TEXT_MAX = 28;

const isTagOrCue = (t: string) => ONLY_BRACKETS_RE.test(t) || ONLY_PAREN_RE.test(t);
const total = (lines: string[]) => lines.join('\n').length;

/** Collapses runs of blank lines left behind by a removal. */
function tidy(lines: string[]): string[] {
  const out: string[] = [];
  for (const l of lines) {
    if (l.trim() === '' && out.length > 0 && out[out.length - 1].trim() === '') continue;
    out.push(l);
  }
  return out;
}

/** Index range [start, end) of a header line plus the tag lines under it, up to a blank line or the next header. */
function blockRange(lines: string[], headerRe: RegExp): [number, number] | null {
  const start = lines.findIndex((l) => headerRe.test(l.trim()));
  if (start < 0) return null;
  let end = start + 1;
  while (end < lines.length) {
    const t = lines[end].trim();
    if (t === '' || t.startsWith('{') || t.startsWith('META_') || t.startsWith('///')) break;
    end++;
  }
  return [start, end];
}

function removeBlock(lines: string[], headerRe: RegExp): string[] {
  const r = blockRange(lines, headerRe);
  if (!r) return lines;
  const [s, e] = r;
  const after = lines[e]?.trim() === '' ? e + 1 : e; // take one trailing blank line with it
  return lines.slice(0, s).concat(lines.slice(after));
}

/** The next non-blank line after `i`, and its index. */
function nextNonBlank(lines: string[], i: number): [string, number] | null {
  for (let k = i + 1; k < lines.length; k++) if (lines[k].trim() !== '') return [lines[k].trim(), k];
  return null;
}

/** Solo block lines [header+1, end): stops at the first sung line or major section tag. */
function soloScan(lines: string[]): { labels: number[]; details: number[] } {
  const labels: number[] = [];
  const details: number[] = [];
  const h = lines.findIndex((l) => SOLO_HEADER_RE.test(l.trim()));
  if (h < 0) return { labels, details };
  for (let j = h + 1; j < lines.length; j++) {
    const t = lines[j].trim();
    if (t === '') continue;
    if (!isTagOrCue(t)) break; // a sung line: the solo block is over
    if (MAJOR_SECTION_RE.test(t) && !BAR_LABEL_RE.test(t)) break;
    if (!BAR_LABEL_RE.test(t)) continue;
    labels.push(j);
    const nxt = nextNonBlank(lines, j);
    if (!nxt) continue;
    const [nt, nk] = nxt;
    const isDetail = isTagOrCue(nt)
      && !BAR_LABEL_RE.test(nt) && !SOLO_INSTRUMENT_RE.test(nt) && !MAJOR_SECTION_RE.test(nt)
      && !STRUCTURE_AFTER_SOLO_RE.test(nt) && !HAS_BAR_COUNT_RE.test(nt);
    if (isDetail) details.push(nk);
  }
  return { labels, details };
}

function dropSoloDetail(lines: string[]): string[] {
  const { details } = soloScan(lines);
  if (details.length === 0) return lines;
  const drop = new Set(details);
  return tidy(lines.filter((_, i) => !drop.has(i)));
}

function clipSoloLabels(lines: string[]): string[] {
  const { labels } = soloScan(lines);
  if (labels.length === 0) return lines;
  const out = [...lines];
  for (const i of labels) {
    const m = out[i].trim().match(/^(\[Bar \d+-\d+:\s*)(.*?)\]$/);
    if (!m) continue;
    out[i] = `${m[1]}${clipAtClause(m[2], LABEL_TEXT_MAX)}]`;
  }
  return out;
}

export interface ForgeSqueezeResult {
  body: string;
  /** Which squeezes ran, in order. */
  steps: string[];
}

/**
 * If `body` is over `absMax`, applies the squeezes above one at a time until it is at or under
 * `targetMax`. Returns the body unchanged when it already fits.
 */
export function squeezeForgeBlocks(body: string, absMax: number, targetMax: number): ForgeSqueezeResult {
  if (body.length <= absMax) return { body, steps: [] };

  let lines = body.split('\n');
  const steps: string[] = [];
  const stages: { name: string; run: (l: string[]) => string[] }[] = [
    { name: 'solo-detail', run: dropSoloDetail },
    { name: 'solo-labels', run: clipSoloLabels },
    { name: 'scream', run: (l) => removeBlock(l, SCREAM_HEADER_RE) },
    ...DEEP_DIVE_LAYER_RES.map((re, i) => ({
      name: ['deep-dive-breath', 'deep-dive-aging', 'deep-dive-dialect'][i],
      run: (l: string[]) => {
        const next = removeBlock(l, re);
        const anyLeft = DEEP_DIVE_LAYER_RES.some((r) => blockRange(next, r));
        return anyLeft ? next : tidy(next.filter((x) => !DEEP_DIVE_HEADER_RE.test(x.trim())));
      },
    })),
  ];

  for (const stage of stages) {
    if (total(lines) <= targetMax) break;
    const next = stage.run(lines);
    if (next.length !== lines.length || next.some((l, i) => l !== lines[i])) {
      lines = next;
      steps.push(stage.name);
    }
  }
  return { body: lines.join('\n'), steps };
}
